import { ActivatedRoute } from '@angular/router';
import { BehaviorSubject, Subject, takeUntil } from 'rxjs';
import { CallRecording } from '../shared/call-recording.model';
import { Component, OnInit, ViewChild, AfterViewInit, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { Disposition } from '../shared/disposition.model';
import { DispositionsService } from '../shared/dispositions.service';
import { Enums, IEnums } from '../../app.enums';
import { FilterService } from '../shared/filter.service';
import { ListRequest, Filter, ListResponse } from 'app/_shared/models/models-index';
import { MatMenu, MatMenuTrigger } from '@angular/material/menu';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { MatTable, MatTableDataSource } from '@angular/material/table';
import { CsvService, CsvHeader, ConfigurationService } from 'app/_shared/services';
import moment from 'moment';

@Component({
    selector: 'dashboard-call-stream',
    templateUrl: './call-stream.component.html',
    styleUrls: ['./call-stream.component.scss']
    // animations: [...] // Remove the animations property
    ,
    standalone: false
})
export class CallStreamComponent implements OnInit, AfterViewInit, OnDestroy {
  dataSource = new MatTableDataSource<Disposition>();
  entities: ListResponse<Disposition> = { records: [] } as ListResponse<Disposition>;
  dispositionCount = 0;
  filters$ = new BehaviorSubject<Filter[]>([]);
  requestModel: ListRequest;
  expandedRowSessionId: string | null = null;
  callRecordingsUrlRoot: string;
  dateFilterStartAt: moment.Moment | Date;
  enums: IEnums = Enums;
  loading: boolean = false;
  sortingInProgress: boolean = false;
  actualDisplayedColumns: string[] = [];

  // Sorting state
  currentSortField: string = 'dispositiontimestamp';
  currentSortDirection: boolean = false; // false = descending, true = ascending

  private destroy$ = new Subject<void>();

  @ViewChild(MatPaginator, { static: true }) paginator: MatPaginator;
  @ViewChild(MatTable) table: MatTable<Disposition>;

  @ViewChild('dateFilterMenu', { static: true }) dateFilterMenu: MatMenu;
  @ViewChild('dateFilterMenuTrigger', { static: true }) dateFilterMenuTrigger: MatMenuTrigger;

  headers: { def: string, showInbound?: boolean }[] = [
    // showInbound === null => display in both types
    { def: 'actionRequired', showInbound: null },
    { def: 'dispositionTimestamp', showInbound: null },
    { def: 'handleTime', showInbound: true },
    { def: 'inboundRetailerCode', showInbound: true },
    { def: 'name', showInbound: true },
    { def: 'vehicle', showInbound: true },
    { def: 'type', showInbound: false },
    { def: 'outboundRetailerCode', showInbound: false },
    { def: 'vin', showInbound: false },
    { def: 'customerName', showInbound: false },
    { def: 'recallCodes', showInbound: false },
    { def: 'dispositionName', showInbound: null },
  ];

  private isInboundRoute: boolean = false;

  constructor(
    private dispositionService: DispositionsService,
    private filterService: FilterService,
    private csvService: CsvService,
    private currentRoute: ActivatedRoute,
    private configService: ConfigurationService,
    private cdRef: ChangeDetectorRef
  ) { }

  async ngOnInit(): Promise<void> {
    this.configService.Config.subscribe(config => {
      if (!config) return;
      this.callRecordingsUrlRoot = config.callRecordingsUrlRoot;
    });

    this.currentRoute.parent.url.subscribe((urlPath) => {
      const url = urlPath[urlPath.length - 1].path;
      if (url === this.enums.dashboardSegment.inbound)
        this.isInboundRoute = true;
      this.actualDisplayedColumns = this.getDisplayedColumns();
    });

    // Initialize request model with default sorting
    this.requestModel = {
      orderBy: this.currentSortField,
      orderAscending: this.currentSortDirection,
      pageNumber: 1,
      pageSize: 20
    };

    this.dispositionService.dispositions.subscribe((dispositions) => {
      if (!dispositions) return;
      this.entities = dispositions;
      
      // Get the program ID from the filter state if it exists
      const programFilter = this.requestModel?.filters?.find(f => f.type === 'ProgramId');
      const programId = programFilter ? parseInt(programFilter.value, 10) : null;
      
      // Filter data by program ID - API should be filtering, but we filter client-side as well for consistency
      const data = programId !== null 
        ? dispositions.records.filter(r => r.programId === programId)
        : dispositions.records; // If no program filter, show all records

      setTimeout(() => {
        if (this.paginator && dispositions) {
          this.paginator.length = dispositions.totalRecords;
        }
      });

      this.dataSource.data = data;
      this.actualDisplayedColumns = this.getDisplayedColumns();
      this.sortingInProgress = false;
    });

    this.filterService.updatedSubject.pipe(takeUntil(this.destroy$)).subscribe(async (filterState) => {
      this.requestModel = filterState;
      this.dateFilterStartAt = this.getStartAtDate();

      // Preserve sorting parameters
      this.requestModel.orderBy = this.currentSortField;
      this.requestModel.orderAscending = this.currentSortDirection;

      if (this.requestModel.filters) {
        this.filters$.next(this.requestModel.filters.filter(f => f.type !== 'RetailerId'))
      }
      if (this.paginator) {
        this.paginator.pageIndex = 0;
      }
      this.requestModel.pageNumber = 0;
      this.actualDisplayedColumns = this.getDisplayedColumns();
      await this.loadDispositions(true);
    });
  }

  async ngAfterViewInit(): Promise<void> {
    if (this.paginator) {
      this.paginator.page.pipe(takeUntil(this.destroy$)).subscribe(async ($event: PageEvent) => {
        this.requestModel.pageNumber = $event.pageIndex;
        this.requestModel.pageSize = $event.pageSize;
        await this.loadDispositions(true);
      });
    }
  }

  async loadDispositions(force: boolean = false): Promise<void> {
    const requestModel = this.getListRequestFromFilters();
    await this.dispositionService.getDispositions(requestModel, force);
  }

  getDisplayedColumns(): string[] {
    return this.headers
      .filter(cd =>
        this.isInboundRoute
          ? (cd.showInbound === true || cd.showInbound === null)
          : (cd.showInbound === false || cd.showInbound === null)
      )
      .map(cd => cd.def);
  }

  getCallRecordingUrl(recording: CallRecording) {
    return `${this.callRecordingsUrlRoot}${recording.blobRelativeUri}`;
  }

  addFilter(filter: Filter) {
    // check to see if we already have a filter of the same type and if so, remove it first
    const filtered = this.filters$.value.filter(f => f.type !== filter.type);

    this.filters$.next([...filtered, filter]);

    this.loadDispositions();
  }

  removeFilter(filter: Filter) {
    this.filters$.next(this.filters$.value.filter(f => f !== filter));

    this.loadDispositions();
  }

  getStartAtDate() {
    const current = this.requestModel.filters.find(f => f.type === 'Date');
    if (current) {
      return moment(current.value).toDate();
    } else if (moment(this.requestModel.endDate) > moment()) {
      return moment();
    } else {
      return moment(this.requestModel.startDate);
    }
  }

  changeDateFilter(date: moment.Moment) {
    this.dateFilterMenuTrigger.closeMenu();
    const dateFilter: Filter = { type: 'Date', value: date.format('MM/DD/YYYY') };
    this.addFilter(dateFilter);
  }

  toggleRowExpansion(row: Disposition): void {
    console.log('[CallStream] Row clicked:', row);
    console.log('[CallStream] Current expandedRowSessionId (before toggle):', this.expandedRowSessionId);
    console.log('[CallStream] Clicked row sessionId:', row.sessionId);

    if (this.expandedRowSessionId === row.sessionId) {
      this.expandedRowSessionId = null;
      console.log('[CallStream] Collapsing row. New expandedRowSessionId:', this.expandedRowSessionId);
    } else {
      this.expandedRowSessionId = row.sessionId;
      console.log('[CallStream] Expanding row. New expandedRowSessionId:', this.expandedRowSessionId);
    }
    this.cdRef.detectChanges();
    if (this.table) {
      console.log('[CallStream] Explicitly calling table.renderRows()');
      this.table.renderRows();
    } else {
      console.warn('[CallStream] MatTable instance not found, cannot call renderRows().');
    }
  }

  async exportToCsv(): Promise<void> {
    this.loading = true;

    try {
      const requestModel = this.getListRequestFromFilters();
      requestModel.ignorePaging = true;
      var dispositions = await this.dispositionService.getDispositions(requestModel, true);
      let csvHeaderDefinitions: CsvHeader<Disposition>[] = [];
      if (this.isInboundRoute) {
        csvHeaderDefinitions = [{
          text: 'Timestamp',
          order: 10,
          valueMap: d => d.dispositionTimestamp
        }, {
          text: 'Duration (seconds)',
          order: 20,
          valueMap: d => d.handleTime
        }, {
          text: 'Retailer Code',
          order: 30,
          valueMap: d => d.retailerCode
        }, {
          text: 'Name',
          order: 40,
          valueMap: d => {
            let value = '';

            if (d.firstName) { value += d.firstName; }
            if (d.lastName) { value += ` ${d.lastName}`; }

            return value.trim();
          }
        }, {
          text: 'Vehicle',
          order: 50,
          valueMap: d => {
            let value = '';

            if (d.vehicleYear) { value += d.vehicleYear; }
            if (d.vehicleMake) { value += ` ${d.vehicleMake}`; }
            if (d.vehicleModel) { value += ` ${d.vehicleModel}`; }

            return value.trim();
          }
        }, {
          text: 'Outcome',
          order: 60,
          valueMap: d => d.dispositionName
        }, {
          text: 'Session ID',
          order: 70,
          valueMap: d => d.sessionId
        }, {
          text: 'Comments',
          order: 80,
          valueMap: d => d.comments
        }];
      } else {
        csvHeaderDefinitions = [{
          text: 'Disposition Date',
          order: 10,
          valueMap: d => d.dispositionTimestamp
        }, {
          text: 'Disposition TYpe',
          order: 20,
          valueMap: d => d.type
        }, {
          text: 'Retailer Code',
          order: 30,
          valueMap: d => d.retailerCode
        }, {
          text: 'Retailer Name',
          order: 40,
          valueMap: d => d.retailerName
        }, {
          text: 'VIN',
          order: 50,
          valueMap: d => d.vin
        }, {
          text: 'Name',
          order: 60,
          valueMap: d => {
            let value = '';

            if (d.firstName) { value += d.firstName; }
            if (d.lastName) { value += ` ${d.lastName}`; }

            return value.trim();
          }
        }, {
          text: 'Recall Code(s)',
          order: 70,
          valueMap: d => d.recallCodes
        }, {
          text: 'Outcome',
          order: 80,
          valueMap: d => d.dispositionName
        }, {
          text: 'Session ID',
          order: 90,
          valueMap: d => d.sessionId
        }, {
          text: 'Comments',
          order: 100,
          valueMap: d => d.comments
        }];
      }

      this.csvService.createAndDownload(csvHeaderDefinitions, dispositions.records, 'call-stream.csv');
    } catch (error) {

    } finally {
      this.loading = false;
    }

  }

  private getListRequestFromFilters(): ListRequest {
    const requestModel = { ...this.requestModel };
    requestModel.filters = [...new Set<Filter>(requestModel.filters.concat(this.filters$.value)).values()];
    
    // Add sorting parameters
    requestModel.orderBy = this.currentSortField;
    requestModel.orderAscending = this.currentSortDirection;

    return requestModel;
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get pageStart(): number {
    return this.paginator && this.paginator.pageIndex !== undefined && this.paginator.pageSize !== undefined
      ? (this.paginator.pageIndex * this.paginator.pageSize) + 1
      : 0;
  }

  get pageEnd(): number {
    return this.paginator && this.paginator.pageIndex !== undefined && this.paginator.pageSize !== undefined && this.paginator.length !== undefined
      ? Math.min((this.paginator.pageIndex + 1) * this.paginator.pageSize, this.paginator.length)
      : 0;
  }

  get pageLength(): number {
    return this.paginator && this.paginator.length !== undefined ? this.paginator.length : 0;
  }

  isDetailRow = (index: number, row: Disposition): boolean => {
    //console.log(`[CallStream] isDetailRow Evaluating for row.sessionId: '${row.sessionId}' (type: ${typeof row.sessionId})`);
    //console.log(`[CallStream] isDetailRow Current this.expandedRowSessionId: '${this.expandedRowSessionId}' (type: ${typeof this.expandedRowSessionId})`);
    const shouldExpand = row.sessionId === this.expandedRowSessionId;
    //console.log(`[CallStream] isDetailRow Result for row.sessionId '${row.sessionId}': shouldExpand = ${shouldExpand}`);
    return shouldExpand;
  }

  // Sorting methods
  sortBy(field: string): void {
    if (this.currentSortField === field) {
      // Toggle direction if same field
      this.currentSortDirection = !this.currentSortDirection;
    } else {
      // New field, start with ascending
      this.currentSortField = field;
      this.currentSortDirection = true;
    }

    // Update request model with sorting parameters
    if (this.requestModel) {
      this.requestModel.orderBy = field;
      this.requestModel.orderAscending = this.currentSortDirection;
    }

    // Reset to first page when sorting
    if (this.paginator) {
      this.paginator.pageIndex = 0;
    }

    // Set sorting in progress
    this.sortingInProgress = true;

    // Reload data with new sorting
    this.loadDispositions(true);
  }

  getSortIcon(field: string): string {
    if (this.currentSortField !== field) {
      return ''; // No icon if not the current sort field
    }
    return this.currentSortDirection ? 'arrow_upward' : 'arrow_downward';
  }
}
